import {RequestBody} from '../../entity/core/request-body';
import {UniqueIdentifier} from '@dnd-kit/core';
import {API} from 'aws-amplify';
import {awsApiName} from '../../shared/amplify/amplify-utils';
import {Title} from '../../entity/model/title';
import axios, {AxiosResponse} from 'axios';
import {ValidateResponse} from '../../entity/resource/validate-response';
import {SplitAIDto} from '../../entity/dto/split-ai-dto';
import {TitleTemplate} from '../../entity/model/title-template';
import {SignedUrlResource} from '../../entity/resource/signed-url-resource';


export class DocumentAgent {

  moveDocument = async (organizationId: string, section: UniqueIdentifier, requestBody: RequestBody, titleId?: string, templateId?: string): Promise<Title | TitleTemplate> => {
    if (titleId)
      return await API.put(awsApiName, `/organizations/${organizationId}/project-titles/${titleId}/sections/${section}/move-document`, requestBody);
    else
      return await API.patch(awsApiName, `/organizations/${organizationId}/title-templates/${templateId}/sections/${section}/move-document`, requestBody);
  }

  deleteDocument = async (organizationId: string, sectionId: string, documentId: UniqueIdentifier, titleId?: string, templateId? : string): Promise<void> => {
    return await API.del(awsApiName, `/organizations/${organizationId}${titleId ? `/project-titles/${titleId}` : `/title-templates/${templateId}`}/sections/${sectionId}/documents/${documentId}`, {});
  }

  updateDocument = async (organizationId: string, sectionId: string, documentId: string, requestBody: RequestBody, titleId?: string, templateId?: string): Promise<Document> => {
    return await API.patch(awsApiName, `/organizations/${organizationId}${titleId ? `/project-titles/${titleId}` : `/title-templates/${templateId}`}/sections/${sectionId}/documents/${documentId}`, requestBody);
  }

  getPreSignedUrl = async (organizationId: string, titleId: string, requestBody: RequestBody): Promise<SignedUrlResource> => {
    try {
      return await API.post(awsApiName, `/organizations/${organizationId}/project-titles/${titleId}/documents/presigned-url`, requestBody);
    } catch (error: any){
      return error?.message;
    }
  }

  getSignedUrl = async (organizationId: string, titleTemplateId: string, requestBody: RequestBody): Promise<SignedUrlResource> => {
    try {
      return await API.post(awsApiName, `/organizations/${organizationId}/title-templates/${titleTemplateId}/documents/signed-url`, requestBody);
    } catch (error: any){
      return error?.message;
    }
  }

  s3FileUpload = async (file: Blob, url: string, setFileProgress?: (progress: number) => void): Promise<AxiosResponse<any>> => {
    const axiosInstance = axios.create();
    return await axiosInstance.put(url, file, {
      headers: {'Content-Type': file.type},
      onUploadProgress: (progressEvent) => {
        const total = progressEvent.total ?? file.size;
        const percentCompleted = Math.round((progressEvent.loaded * 100) / total);

        if (setFileProgress)
          setFileProgress(percentCompleted);
      }
    });
  }

  uploadDocument = async (organizationId: string, titleId: string, requestBody: RequestBody, sectionId?: string): Promise<any> => {
    return await API.post(awsApiName, `/organizations/${organizationId}/project-titles/${titleId}/${sectionId ? `sections/${sectionId}/document` : 'documents'}`, requestBody);
  }

  createMergePdf = async (organizationId: string, titleId: string, documentIDs: string[], batchID: string): Promise<any> => {
    const requestBody = {
      uploadType: 'DOCUMENT',
      mimeType: 'application/pdf',
      documentIDs: documentIDs,
      batchID: batchID
    };
    return await API.post(awsApiName, `/organizations/${organizationId}/extended-operations/project-titles/${titleId}/documents/merge`, { body: requestBody });
  }

  validateDocuments =  async (organizationId: string, titleId: string, documentIDs: string[], batchId: string): Promise<ValidateResponse> => {
    const requestBody = { uploadType: 'DOCUMENT', mimeType: 'application/pdf', documentIDs: documentIDs, batchID: batchId };
    return await API.post(awsApiName, `/organizations/${organizationId}/extended-operations/project-titles/${titleId}/documents/validate`, { body: requestBody });
  }

  splitDocument =  async (organizationId: string, titleId: string, requestBody: RequestBody): Promise<any> => {
    return await API.post(awsApiName, `/organizations/${organizationId}/extended-operations/project-titles/${titleId}/documents/split`, requestBody);
  }

  documentRunner = async (organizationId: string, requestBody: RequestBody): Promise<any> => {
    return await API.post(awsApiName, `/organizations/${organizationId}/async-job/webscraper`, requestBody);
  }

  splitWithAI =  async (requestBody: SplitAIDto): Promise<any> => {
    return await axios.post(process.env.REACT_APP_AI_SPLIT_ENDPOINT ?? 'https://dev.data.zitles.com/ai/classify/document' , requestBody);
  }

  publishDocument =  async (organizationId: string, titleTemplateId: string, titleId: string, requestBody: RequestBody): Promise<any> => {
    return await API.patch(awsApiName, `/organizations/${organizationId}/title-templates/${titleTemplateId}/import/from-title/${titleId}`, requestBody);
  }
}
